import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { BOT_CONFIG } from './MTModule';

/**
 * Trade Stream Live — last N closed trades across ALL connected MT accounts
 * displayed as an ECG/heartbeat trace + scrolling ticker.
 *
 * Synthesized deterministically per-account from login seed
 * (real closed trades require MetaApi `getDealsByTimeRange`).
 */

const SYMBOLS = ['EURUSD', 'GBPUSD', 'USDJPY', 'XAUUSD', 'BTCUSD', 'ETHUSD', 'NAS100', 'US30', 'SPX500', 'XAGUSD'];

const seedHash = (s) => {
  let h = 0;
  const str = String(s || 'x');
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h);
};
const mulb = (a) => () => {
  a |= 0; a = (a + 0x6D2B79F5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Synthesize the last 8 closed trades for an account, deterministically
const synthesizeTradesForAccount = (acc) => {
  const baseSeed = seedHash(acc.login || acc.mt_id);
  const rand = mulb(baseSeed);
  const out = [];
  const bias = (acc.info?.profit || 0) >= 0 ? 0.58 : 0.42; // overall winning accounts trade better
  const balance = Math.max(acc.info?.balance || 5000, 1000);
  const now = Date.now();
  for (let i = 0; i < 8; i++) {
    const win = rand() < bias;
    const sign = win ? 1 : -1;
    const mag = (rand() * 0.025 + 0.002) * balance;
    const profit = sign * mag;
    const symbol = SYMBOLS[Math.floor(rand() * SYMBOLS.length)];
    const side   = rand() < 0.5 ? 'buy' : 'sell';
    const minutesAgo = Math.floor(rand() * 60 * 24); // last 24h
    out.push({
      id:       `${acc.mt_id}-${i}`,
      botKey:   acc.ai_bot || 'custom',
      login:    acc.login,
      symbol,
      side,
      profit,
      closedAt: new Date(now - minutesAgo * 60 * 1000),
    });
  }
  return out;
};

const fmtUsd = (v) => (v >= 0 ? '+' : '') + '$' + Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 });
const ago = (date) => {
  const sec = (Date.now() - date.getTime()) / 1000;
  if (sec < 60)   return `${Math.floor(sec)}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

const TradeStreamLive = ({ accounts = [] }) => {
  const [tick, setTick] = useState(0);
  // Re-render every 30s for relative-time labels
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const trades = useMemo(() => {
    void tick; // touch tick so re-render refreshes "ago" labels
    const all = [];
    accounts.forEach((acc) => all.push(...synthesizeTradesForAccount(acc)));
    all.sort((a, b) => b.closedAt - a.closedAt);
    return all.slice(0, 24); // newest first
  }, [accounts, tick]);

  if (!accounts.length || !trades.length) return null;

  /* ─── ECG Path: maps each trade to a heartbeat spike along a horizontal axis ─── */
  const ecgW = 1200, ecgH = 120, midY = ecgH / 2;
  const maxAbs = Math.max(1, ...trades.map((t) => Math.abs(t.profit)));
  const visibleTrades = trades.slice(0, 18); // most-recent shown on the trace
  const dx = ecgW / visibleTrades.length;

  // Build a stylized ECG: flat baseline broken by sharp PQRS-like spikes per trade
  const pathPoints = (() => {
    let d = `M 0 ${midY}`;
    visibleTrades.forEach((t, i) => {
      const xStart = i * dx;
      const xPeak  = xStart + dx * 0.4;
      const xBack  = xStart + dx * 0.55;
      const xEnd   = xStart + dx;
      const intensity = (Math.abs(t.profit) / maxAbs);
      const amp = 8 + intensity * 38; // 8..46
      const spikeY = t.profit >= 0 ? midY - amp : midY + amp;
      const dipY   = t.profit >= 0 ? midY + amp * 0.25 : midY - amp * 0.25;
      // little Q (small dip), then sharp R (spike), then S (overshoot opposite), then baseline
      d += ` L ${(xStart + dx * 0.30).toFixed(1)} ${(midY).toFixed(1)}`;
      d += ` L ${(xStart + dx * 0.35).toFixed(1)} ${(dipY).toFixed(1)}`;
      d += ` L ${xPeak.toFixed(1)} ${spikeY.toFixed(1)}`;
      d += ` L ${xBack.toFixed(1)} ${(midY - (spikeY - midY) * 0.20).toFixed(1)}`;
      d += ` L ${xEnd.toFixed(1)} ${midY.toFixed(1)}`;
    });
    return d;
  })();

  return (
    <Card
      className="overflow-hidden border"
      style={{
        background: 'linear-gradient(180deg, rgba(34,197,94,0.04), rgba(4,8,22,0.92))',
        borderColor: 'rgba(34,197,94,0.30)',
        boxShadow: '0 18px 60px -25px rgba(34,197,94,0.45)',
      }}
      data-testid="trade-stream-live"
    >
      <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(34,197,94,0.20)' }}>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#22c55e]" />
          </span>
          <Activity className="w-4 h-4 text-[#22c55e]" />
          <h3 className="text-base font-bold text-white">Trade Stream Live</h3>
          <span className="text-[10px] tracking-[0.25em] uppercase text-[#22c55e]">ECG · last 24h</span>
        </div>
        <div className="text-[10px] text-[#556475] tracking-[0.25em] uppercase">{trades.length} trades</div>
      </div>

      <CardContent className="p-0">
        {/* ── ECG TRACE ── */}
        <div
          className="relative"
          style={{
            background: 'radial-gradient(800px 200px at 50% 50%, rgba(34,197,94,0.06), transparent 70%), repeating-linear-gradient(0deg, rgba(34,197,94,0.05) 0px, rgba(34,197,94,0.05) 1px, transparent 1px, transparent 14px), repeating-linear-gradient(90deg, rgba(34,197,94,0.05) 0px, rgba(34,197,94,0.05) 1px, transparent 1px, transparent 14px)',
          }}
        >
          <svg viewBox={`0 0 ${ecgW} ${ecgH}`} className="w-full block" preserveAspectRatio="none" style={{ height: ecgH }}>
            <defs>
              <linearGradient id="ecg-stroke" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="rgba(34,197,94,0.2)" />
                <stop offset="40%" stopColor="#22c55e" />
                <stop offset="100%" stopColor="#00f4ff" />
              </linearGradient>
            </defs>

            {/* baseline */}
            <line x1="0" y1={midY} x2={ecgW} y2={midY} stroke="rgba(34,197,94,0.15)" strokeWidth="0.5" strokeDasharray="6 6" />

            {/* trace */}
            <path
              d={pathPoints}
              fill="none"
              stroke="url(#ecg-stroke)"
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              style={{
                filter: 'drop-shadow(0 0 6px rgba(34,197,94,0.55))',
                strokeDasharray: 4000,
                strokeDashoffset: 0,
                animation: 'sim360-ecg-scan 0.9s steps(60) infinite',
              }}
            />

            {/* per-trade dots at peaks */}
            {visibleTrades.map((t, i) => {
              const xPeak = i * dx + dx * 0.4;
              const amp = 8 + (Math.abs(t.profit) / maxAbs) * 38;
              const y = t.profit >= 0 ? midY - amp : midY + amp;
              const color = t.profit >= 0 ? '#22c55e' : '#ef4444';
              return (
                <g key={t.id}>
                  <circle cx={xPeak} cy={y} r="2.5" fill={color}
                    style={{ filter: `drop-shadow(0 0 8px ${color})` }} />
                </g>
              );
            })}

            {/* moving sweep line */}
            <line x1="0" y1="0" x2="0" y2={ecgH} stroke="#22c55e" strokeWidth="1.2" opacity="0.7"
              style={{
                filter: 'drop-shadow(0 0 6px #22c55e)',
                animation: `sim360-ecg-sweep 6s linear infinite`,
              }} />
          </svg>
        </div>

        {/* ── TICKER (horizontal scroll) ── */}
        <div className="relative overflow-hidden border-t border-[#0b1c38]" style={{ background: 'rgba(4,8,22,0.6)' }}>
          <div className="absolute inset-y-0 left-0 w-14 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(90deg, rgba(4,8,22,1), transparent)' }} />
          <div className="absolute inset-y-0 right-0 w-14 z-10 pointer-events-none"
            style={{ background: 'linear-gradient(270deg, rgba(4,8,22,1), transparent)' }} />
          <div
            className="flex gap-3 py-3 px-4"
            style={{
              animation: `sim360-ticker-scroll ${Math.max(40, trades.length * 4)}s linear infinite`,
              width: 'max-content',
            }}
          >
            {[...trades, ...trades].map((t, idx) => {
              const isWin = t.profit >= 0;
              const color = isWin ? '#22c55e' : '#ef4444';
              const botCfg = BOT_CONFIG[t.botKey] || { color: '#9EB4C0', label: t.botKey };
              return (
                <div
                  key={`${t.id}-${idx}`}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-mono whitespace-nowrap"
                  style={{
                    background: `${color}10`,
                    border: `1px solid ${color}40`,
                    boxShadow: `0 0 14px -8px ${color}`,
                  }}
                  data-testid="trade-ticker-chip"
                >
                  {isWin
                    ? <ArrowUpRight className="w-3.5 h-3.5" style={{ color }} />
                    : <ArrowDownRight className="w-3.5 h-3.5" style={{ color }} />}
                  <span className="font-bold" style={{ color }}>{fmtUsd(t.profit)}</span>
                  <span className="text-white">{t.symbol}</span>
                  <span className="text-[#556475] uppercase">{t.side}</span>
                  <span
                    className="px-1.5 py-0.5 rounded text-[9px] tracking-[0.2em] uppercase font-bold"
                    style={{ background: `${botCfg.color}1A`, color: botCfg.color, border: `1px solid ${botCfg.color}40` }}
                  >
                    {botCfg.label}
                  </span>
                  <span className="text-[#556475] text-[10px]">· {ago(t.closedAt)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── footer note ── */}
        <div className="px-5 py-2.5 flex items-center justify-between text-[10px] text-[#556475] tracking-[0.2em] uppercase border-t border-[#0b1c38]">
          <span>● live · auto-refresh 30s</span>
          <span className="italic normal-case tracking-normal">
            Synthesized stream — real deals require MetaApi `getDealsByTimeRange`
          </span>
        </div>
      </CardContent>

      <style>{`
        @keyframes sim360-ecg-sweep {
          0%   { transform: translateX(0); }
          100% { transform: translateX(${ecgW}px); }
        }
        @keyframes sim360-ecg-scan {
          0%   { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -100; }
        }
        @keyframes sim360-ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </Card>
  );
};

export default TradeStreamLive;
